<template>
  <div v-if="loaded" class="py-4">
    <button
      class="flex items-center gap-1 px-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      @click="goBack"
    >
      <vue-feather type="chevron-left" class="!h-4 !w-4" />
      Back
    </button>

    <div
      v-if="isProcessing"
      class="w-11/12 mx-auto bg-blue-100 text-blue-900 p-4 lg:flex items-center justify-left gap-2 rounded-lg my-8 text-center lg:text-left"
    >
      <vue-feather type="alert-triangle" class="h-8 mx-auto lg:mx-0" />
      <div class="lg:ml-8">
        <header class="font-bold">Recording is processing</header>
        <p class="mt-1">Pipeline review recording, transcript, summary, and coaching will be available soon.</p>
      </div>
    </div>

    <div class="w-full">
      <div class="my-6 w-11/12 mx-auto">
        <MeetingDashboardHeader :contact-event="contactEvent" :contact="contact">
          <template #attendees>
            <EventRecordingAttendees :event-recording="contactEvent.event_recording" />
          </template>
        </MeetingDashboardHeader>
      </div>

      <div class="flex flex-col lg:flex-row items-start gap-8 w-11/12 mx-auto">
        <div class="lg:w-1/2 pr-12">
          <Interview
            v-if="!videoIsReady"
            :contact-event-location="contactEventLocation"
            :is-humaniz-conferencing="isHumanizConferencing"
            :event="contactEvent"
          />
          <video
            v-else
            :src="contactEvent.event_recording.daily_recording_url"
            controls
            class="h-[440px] border rounded-lg border-gray-300 bg-gray-200"
          />
          <History :teammate="contact" :contact-event="contactEvent" />
        </div>
        <div class="lg:lg:w-1/2">
          <Tabs default-value="scorecard">
            <TabsList>
              <TabsTrigger value="scorecard">Scorecard</TabsTrigger>
              <TabsTrigger v-if="isReady" value="transcript">Transcript</TabsTrigger>
              <TabsTrigger v-if="isReady" value="summary">Summary</TabsTrigger>
            </TabsList>
            <TabsContent value="scorecard">
              <div class="flex flex-col overflow-scroll">
                <ReviewDisplay v-if="pipelineReview" :pipeline-review="pipelineReview" :teammate="contact" />
                <ReviewForm v-else :teammate="contact" :contact-event="contactEvent" @created="$router.go(0)" />
              </div>
            </TabsContent>
            <TabsContent value="transcript">
              <div class="flex flex-col overflow-scroll pt-4">
                <TranscriptViewer :transcript-string="contactEvent.event_recording.daily_transcription" />
              </div>
            </TabsContent>
            <TabsContent value="summary">
              <div class="flex flex-col overflow-scroll pt-4">
                {{ contactEvent.event_recording.daily_summary }}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Interview from './Interview.vue';
import TranscriptViewer from '../TranscriptViewer.vue';
import ReviewForm from '@/components/shared/pipeline_reviews/ReviewForm.vue';
import History from './History.vue';
import ReviewDisplay from '@/components/shared/pipeline_reviews/ReviewDisplay.vue';
import EventRecordingAttendees from '@/components/EventRecordingAttendees/index.vue';
import MeetingDashboardHeader from '@/components/contact_events/MeetingDashboardHeader.vue';
import { useRouter } from 'vue-router';

export default {
  components: {
    Interview,
    TranscriptViewer,
    ReviewForm,
    History,
    ReviewDisplay,
    EventRecordingAttendees,
    MeetingDashboardHeader,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
  },
  setup() {
    const router = useRouter();
    const goBack = () => {
      if (window.history.state.back) {
        router.back();
      } else {
        router.push({ name: 'Dashboard' });
      }
    };
    return { router, goBack };
  },
  data() {
    return {
      loaded: false,
      contactEvent: null,
      contact: null,
      scripts: {
        team: null,
        global: null,
      },
      showDaily: false,
      pipelineReview: null,
    };
  },
  computed: {
    contactEventLocation() {
      return this.contactEvent.location;
    },
    isHumanizConferencing() {
      if (this.contactEventLocation) {
        return this.contactEventLocation.includes('humaniz') || this.contactEventLocation.includes('daily.co');
      } else {
        return false;
      }
    },
    isProcessing() {
      const isPast = new Date(this.contactEvent.start_time) < new Date();

      return isPast && this.isHumanizConferencing && !this.isReady;
    },
    isReady() {
      if (!this.contactEvent.event_recording) return false;

      const recordingUrl = this.contactEvent?.event_recording?.daily_recording_url;
      const score = this.contactEvent.event_recording.score;

      return recordingUrl && !isEmpty(score);
    },
    videoIsReady() {
      if (!this.contactEvent.event_recording) return false;
      if (!this.contactEvent.event_recording.daily_recording_url) return false;
      if (new Date(this.contactEvent.end_time) > new Date()) return false;

      return true;
    },
  },
  async mounted() {
    document.body.classList.remove('lg:overflow-hidden');
    await this.reload();
    await this.fetchScripts();
    this.loaded = true;
  },
  methods: {
    async reload() {
      const res = await fetch(`/api/contact_events/${this.$route.params.id}`);
      const { contact, ...contactEvent } = await res.json();

      this.contactEvent = contactEvent;
      this.contact = contact;

      this.pipelineReview = contactEvent.pipeline_review;
    },
    async fetchScripts() {
      const res = await fetch(`/api/interview_scripts`);
      const { records } = await res.json();

      this.scripts.team = records.team;
      this.scripts.global = records.global;
    },
  },
};
</script>
